import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { PagePath } from '@monorepo/client-types';
import { SystemIcon } from '@monorepo/nova-icons';
import { Button, Typography } from '@monorepo/nova-ui';
import { TerminationReason } from '@monorepo/types';
import { useTerminationReasonModalState } from './hooks';
import {
  StyledActionsRow,
  StyledCloseButton,
  StyledDialog,
  StyledModalBody,
  StyledTextGroup,
  StyledWarningIconContainer,
} from './styled';

/** Icon sizes derived from theme spacing (SystemIcon expects pixel number). */
const getTerminationModalIconSizes = (theme: {
  spacing: (n: number) => string;
}) => ({
  closeIcon: Number(String(theme.spacing(5)).replace(/px/g, '')) || 20,
  warningIcon: Number(String(theme.spacing(24.5)).replace(/px/g, '')) || 98,
});

export const TerminationReasonModal: FC = () => {
  const { t } = useTranslation('terminationReasonModal');
  const theme = useTheme();
  const { isOpen, terminationReason, dismiss } =
    useTerminationReasonModalState();
  const iconSizes = getTerminationModalIconSizes(theme);

  if (!isOpen || !terminationReason) {
    return null;
  }

  const isOverNight = terminationReason === TerminationReason.OverNight;
  const title = isOverNight ? t('overNightTitle') : t('weekendTitle');
  const message = isOverNight ? t('overNightMessage') : t('weekendMessage');

  return (
    <StyledDialog
      open={isOpen}
      onClose={() => dismiss()}
      maxWidth={false}
      variant="bare"
      aria-labelledby="termination-reason-modal-title"
    >
      <StyledModalBody>
        <StyledCloseButton
          aria-label={t('actions.close')}
          onClick={() => dismiss()}
        >
          <SystemIcon
            iconName="X"
            size={iconSizes.closeIcon}
            color={theme.palette.primary.main}
          />
        </StyledCloseButton>

        <StyledWarningIconContainer>
          <SystemIcon
            iconName="WarningDiamond"
            size={iconSizes.warningIcon}
            color={theme.palette.text.opposite}
          />
        </StyledWarningIconContainer>

        <StyledTextGroup>
          <Typography
            id="termination-reason-modal-title"
            variant="h3"
            fontWeight={700}
          >
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {message}
          </Typography>
        </StyledTextGroup>

        <StyledActionsRow>
          <Button
            variant="primary"
            size="xl"
            onClick={() => dismiss(PagePath.Purchase)}
          >
            {t('actions.getNewAccount')}
          </Button>
          <Button variant="outlined" size="xl" onClick={() => dismiss()}>
            {t('actions.backToDashboard')}
          </Button>
        </StyledActionsRow>
      </StyledModalBody>
    </StyledDialog>
  );
};
